import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import { COMPANY } from "@/data/company";

export default function PrivacyPolicy() {
  return (
    <div>
      <PageHero
        eyebrow="Legal"
        title="Privacy Policy"
        description="Effective date: 1 April 2026 · Your privacy matters to us. This policy explains how we handle your information."
      />
      <section className="section bg-white">
        <article className="container-nago prose prose-slate max-w-3xl">
          <p>
            Nago International (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;) respects your privacy and is committed to
            protecting the personal information you share with us. This Privacy Policy explains
            what information we collect through <strong>{COMPANY.website}</strong> and our
            business interactions, how we use it, and the choices you have.
          </p>

          <LegalSection n="01" title="Introduction & scope">
            This policy applies to personal information we collect through our website, enquiry
            and quotation forms, email, telephone and other business communications. It is
            intended to comply with applicable Indian data-protection law, including the{" "}
            <strong>Digital Personal Data Protection Act, 2023</strong> and the{" "}
            <strong>Information Technology Act, 2000</strong> and rules made thereunder. By using
            our website or providing your information, you consent to the practices described
            here.
          </LegalSection>

          <LegalSection n="02" title="Information we collect">
            <p>
              We collect only the information reasonably necessary to respond to your enquiries
              and serve you as a customer or business partner. This may include:
            </p>
            <div className="not-prose overflow-x-auto border border-slate-200 rounded-xl my-6">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-5 py-3 font-semibold text-slate-700">Category</th>
                    <th className="text-left px-5 py-3 font-semibold text-slate-700">Examples</th>
                  </tr>
                </thead>
                <tbody className="text-slate-700">
                  <Row a="Contact & identity data" b="Name, company name, designation, email address, phone number, billing/shipping address" />
                  <Row a="Business & transaction data" b="Enquiry details, product requirements, quotations, orders, GST number, and correspondence with us" />
                  <Row a="Technical & usage data" b="IP address, browser type, device information, pages visited and referring URLs" />
                  <Row a="Communications" b="Messages you send us via forms, email, WhatsApp or phone" />
                </tbody>
              </table>
            </div>
            <p>
              We do not intentionally collect sensitive personal data through the website. Please
              do not send us payment-card numbers, passwords or other sensitive details through
              website forms or email.
            </p>
          </LegalSection>

          <LegalSection n="03" title="How we collect it">
            <ul>
              <li><strong>Directly from you</strong> — when you complete an enquiry or quotation form, email or call us, or place an order;</li>
              <li><strong>Automatically</strong> — through cookies and similar technologies when you browse the website;</li>
              <li><strong>From third parties</strong> — where you are introduced to us by a partner, or through publicly available business sources.</li>
            </ul>
          </LegalSection>

          <LegalSection n="04" title="How we use your information">
            <ul>
              <li>Respond to your enquiries and provide quotations, product information and technical support;</li>
              <li>Process, fulfil and deliver your orders, and manage payments and invoicing;</li>
              <li>Communicate with you about your enquiries, orders and our products and services;</li>
              <li>Maintain our business records and comply with legal, tax and regulatory obligations;</li>
              <li>Operate, maintain, secure and improve our website;</li>
              <li>Send you updates or offers where you have opted in to receive them.</li>
            </ul>
          </LegalSection>

          <LegalSection n="05" title="Consent & legal basis">
            We process your personal data on the basis of your consent, to fulfil a contract or
            take steps at your request before entering into a contract, to comply with legal
            obligations, and for our legitimate business interests. Where we rely on consent, you
            may withdraw it at any time by contacting us (see section 14); withdrawal does not
            affect processing carried out before withdrawal.
          </LegalSection>

          <LegalSection n="06" title="Cookies & tracking">
            Our website may use cookies and similar technologies to make the site work, remember
            your preferences, and understand how the site is used. This may include essential
            cookies and, where enabled, analytics cookies. You can control or disable cookies
            through your browser settings; disabling some cookies may affect how the website
            functions.
          </LegalSection>

          <LegalSection n="07" title="Sharing & disclosure">
            We do not sell your personal information. We may share it with service providers,
            manufacturers or logistics partners strictly as needed to fulfil your enquiry, order
            or a legal obligation, under confidentiality safeguards.
          </LegalSection>

          <LegalSection n="08" title="Data security">
            We implement reasonable technical and organizational measures to protect your
            personal information against unauthorized access, alteration, disclosure or
            destruction. However, no method of transmission over the internet or electronic
            storage is completely secure, and we cannot guarantee absolute security.
          </LegalSection>

          <LegalSection n="09" title="Data retention">
            We retain personal information only for as long as necessary to fulfil the purposes
            for which it was collected, including to satisfy legal, tax, accounting or reporting
            requirements.
          </LegalSection>

          <LegalSection n="10" title="Your rights">
            <ul>
              <li>Access the personal information we hold about you and request a summary of processing;</li>
              <li>Request correction of inaccurate or incomplete information;</li>
              <li>Request erasure of your personal information where it is no longer required;</li>
              <li>Withdraw consent where processing is based on consent;</li>
              <li>Nominate another person to exercise your rights in the event of death or incapacity;</li>
              <li>Raise a grievance regarding our handling of your data.</li>
            </ul>
          </LegalSection>

          <LegalSection n="11" title="Children's privacy">
            Our website and services are intended for businesses and adult professionals. We do
            not knowingly collect personal information from children.
          </LegalSection>

          <LegalSection n="12" title="Third-party links">
            Our website may link to third-party websites. We are not responsible for the privacy
            practices or content of those sites.
          </LegalSection>

          <LegalSection n="13" title="Changes to this policy">
            We may update this Privacy Policy from time to time to reflect changes in our
            practices or applicable law. The updated version will be posted on this page.
          </LegalSection>

          <LegalSection n="14" title="Grievance officer & contact">
            <p>
              If you have questions about this policy, wish to exercise your rights, or want to
              raise a concern about how we handle your personal data, please contact:
            </p>
            <p>
              <strong>Grievance Officer — Nago International</strong>
              <br />
              {COMPANY.address}
              <br />
              Email: <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>
              <br />
              Phone: {COMPANY.phones.join(" · ")}
              <br />
              WhatsApp: <a href={COMPANY.whatsappLink}>{COMPANY.whatsapp}</a>
              <br />
              Business hours: {COMPANY.hours}
            </p>
          </LegalSection>

          <p className="text-sm text-slate-500 mt-10">
            See also our{" "}
            <Link to="/terms" className="text-[#1E3A8A] font-semibold underline">
              Terms &amp; Conditions
            </Link>{" "}
            and{" "}
            <Link to="/disclaimer" className="text-[#1E3A8A] font-semibold underline">
              Disclaimer
            </Link>
            .
          </p>
        </article>
      </section>
    </div>
  );
}

function LegalSection({ n, title, children }) {
  return (
    <section className="mt-10">
      <h2 className="!mt-0 flex items-baseline gap-3 !text-xl !font-display !font-extrabold text-slate-900">
        <span className="text-[#E30613] font-mono text-sm">{n}</span>
        {title}
      </h2>
      <div className="mt-3 text-slate-700 leading-relaxed">{children}</div>
    </section>
  );
}

function Row({ a, b }) {
  return (
    <tr className="border-t border-slate-200">
      <td className="px-5 py-3 font-semibold align-top">{a}</td>
      <td className="px-5 py-3 align-top">{b}</td>
    </tr>
  );
}
